import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { escriturasService } from '../services/escriturasService';
import type { EscrituraAdicional } from '../types';
import { MAX_ESCRITURA_FILE_SIZE_BYTES, MAX_ESCRITURA_FILE_SIZE_MB } from '@/common/constants';

const ALLOWED_MIME = ['application/pdf'];

interface UseEscriturasAdicionalesProps {
  tramiteId: string | number;
}

export const useEscriturasAdicionales = ({ tramiteId }: UseEscriturasAdicionalesProps) => {
  const queryClient = useQueryClient();
  const queryKey = ['tramite-escrituras', String(tramiteId)];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => escriturasService.list(tramiteId),
    enabled: !!tramiteId,
    refetchOnWindowFocus: false,
  });

  const escrituras = data?.data ?? [];
  const total = data?.total ?? 0;

  const uploadMutation = useMutation({
    mutationFn: ({ file, descripcion }: { file: File; descripcion: string }) =>
      escriturasService.upload(tramiteId, file, descripcion),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (escrituraId: number) => escriturasService.delete(escrituraId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const validateFile = useCallback((file: File): { valid: boolean; error?: string } => {
    if (file.size > MAX_ESCRITURA_FILE_SIZE_BYTES) {
      return { valid: false, error: `El archivo excede el tamaño máximo de ${MAX_ESCRITURA_FILE_SIZE_MB}MB` };
    }
    if (!ALLOWED_MIME.includes(file.type)) {
      return { valid: false, error: 'Solo se permiten archivos PDF' };
    }
    return { valid: true };
  }, []);

  const uploadEscritura = useCallback(
    async (file: File, descripcion: string) => {
      const validation = validateFile(file);
      if (!validation.valid) throw new Error(validation.error);
      return uploadMutation.mutateAsync({ file, descripcion });
    },
    [uploadMutation, validateFile]
  );

  const deleteEscritura = useCallback(
    (escrituraId: number) => deleteMutation.mutateAsync(escrituraId),
    [deleteMutation]
  );

  const downloadEscritura = useCallback(async (escritura: EscrituraAdicional) => {
    await escriturasService.download(escritura);
  }, []);

  return {
    escrituras,
    total,
    isLoading,
    error,
    refetch,
    uploadEscritura,
    deleteEscritura,
    downloadEscritura,
    validateFile,
    isUploading: uploadMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
